import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import { getArticleBySlug, getRelatedArticles, categories } from '@/mocks/articles';
import ArticleCard from '@/components/feature/ArticleCard';
import NotFound from '@/pages/NotFound';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const article = slug ? getArticleBySlug(slug) : null;

  if (!article) {
    return <NotFound />;
  }

  const relatedArticles = getRelatedArticles(article.id);
  const category = categories.find((c) => c.id === article.category);

  return (
    <main className="pt-20 min-h-screen bg-background-50">
      {/* Breadcrumb */}
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-4">
        <nav className="flex items-center gap-2 text-xs text-foreground-400 flex-wrap" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-foreground-700 transition-colors whitespace-nowrap">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          {category && (
            <>
              <Link to={`/category/${category.slug}`} className="hover:text-foreground-700 transition-colors whitespace-nowrap">
                {category.name}
              </Link>
              <i className="ri-arrow-right-s-line"></i>
            </>
          )}
          <span className="text-foreground-700 line-clamp-1">{article.title}</span>
        </nav>
        {/* Schema.org */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": article.title,
          "description": article.excerpt,
          "author": { "@type": "Person", "name": article.author },
          "datePublished": article.publishDate,
          "publisher": {
            "@type": "Organization",
            "name": "玉蒲团文化研究中心",
            "url": "https://huayuep.github.io"
          },
          "inLanguage": "zh-Hans"
        }) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://huayuep.github.io/" },
            ...(category ? [{ "@type": "ListItem", "position": 2, "name": category.name, "item": `https://huayuep.github.io/category/${category.slug}` }] : []),
            { "@type": "ListItem", "position": category ? 3 : 2, "name": article.title }
          ]
        }) }} />
      </div>

      {/* Article Header */}
      <article className="max-w-4xl mx-auto px-4 md:px-6">
        <header className="py-8 md:py-12 border-b border-background-200/70">
          {category && (
            <Link
              to={`/category/${category.slug}`}
              className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-medium mb-4 hover:bg-accent-200 transition-colors"
            >
              {category.name}
            </Link>
          )}
          <h1 className="font-heading font-black text-2xl md:text-4xl text-foreground-950 mb-4 leading-tight">
            {article.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-400">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line"></i>
              {article.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line"></i>
              {article.publishDate}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line"></i>
              {article.readTime}
            </span>
          </div>
          {article.tags && article.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-2.5 py-1 rounded-md bg-background-100 text-foreground-500 text-xs"
                >
                  #{tag}
                </span>
              ))}
            </div>
          )}
        </header>

        {/* Featured Image */}
        <div className="mt-8 rounded-lg overflow-hidden">
          <img
            src={article.imageUrlLarge || article.imageUrl}
            alt={article.title}
            title={`${article.title} - 玉蒲团文化研究`}
            className="w-full h-auto max-h-[400px] object-cover object-top rounded-lg"
          />
        </div>

        {/* Article Content */}
        <div
          className="article-content py-8 md:py-12"
          dangerouslySetInnerHTML={{
            __html: article.content
              .replace(/## (.+)/g, '<h2>$1</h2>')
              .replace(/\n\n(.+)/g, '<p>$1</p>')
              .replace(/<p>- (.+)<\/p>/g, '<p>— $1</p>')
          }}
        />

        {/* Author Box */}
        <div className="mt-10 p-6 rounded-lg bg-background-100 border border-background-200/70 flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
            <i className="ri-user-line text-primary-600 text-xl"></i>
          </div>
          <div>
            <h4 className="font-heading font-semibold text-sm text-foreground-900 mb-1">
              {article.author}
            </h4>
            <p className="text-foreground-500 text-xs leading-relaxed">
              玉蒲团文化研究中心特约作者，长期从事华语情色文学与电影文化研究。
            </p>
          </div>
        </div>
      </article>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
          <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-8">
            相关推荐
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {relatedArticles.map((related) => (
              <ArticleCard
                key={related.id}
                title={related.title}
                slug={related.slug}
                excerpt={related.excerpt}
                imageUrl={related.imageUrl}
                author={related.author}
                publishDate={related.publishDate}
                readTime={related.readTime}
                categoryName={categories.find((c) => c.id === related.category)?.name}
              />
            ))}
          </div>
        </section>
      )}

      {/* Back to top */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 pb-10 text-center">
        <Link
          to="/"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-background-100 text-foreground-600 text-sm hover:bg-background-200/70 transition-colors"
        >
          <i className="ri-arrow-left-line"></i>
          返回首页
        </Link>
      </div>
    </main>
  );
}